<template>
  <div class="cb-device-test-fetch">
    <div class="cb-device-test-fetch__container">
      <div class="cb-device-test-fetch__progress">
        <svg
          class="cb-device-test-fetch__progress__svg"
          viewBox="0 0 100 100"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g class="cb-device-test-fetch__progress__circle">
            <circle
              class="cb-device-test-fetch__progress__path-elapsed"
              cx="50"
              cy="50"
              r="45"
            ></circle>
            <path
              :stroke-dasharray="circleDasharray"
              :style="circleAnimation"
              ref="progressBar"
              class="cb-device-test-fetch__progress__path-remaining"
              d="
            M 50, 50
            m -45, 0
            a 45,45 0 1,0 90,0
            a 45,45 0 1,0 -90,0
          "
            ></path>
          </g>
        </svg>
        <h1
          class="cb-device-test-fetch__heading cb-heading-3 cb-device-test-fetch__progress__label"
          tabindex="-1"
          ref="title"
          role="alert"
        >
          <span>
            {{ title }}
          </span>
        </h1>
      </div>
      <div
        class="sr-only"
        role="progressbar"
        :aria-valuenow="progress"
        aria-valuemin="0"
        aria-valuemax="100"
      >
        {{ progress }}% Complete
      </div>
      <p v-html="body" class="cb-device-test-fetch__body"></p>
    </div>
    <div class="cb-device-test-fetch__footer" v-if="manualTestRequired">
      <div class="cb-device-test-fetch__divider"></div>
      <button
        @click="handleBackClick"
        class="cb-button cb-button--outline-white"
      >
        Go Back
      </button>
    </div>
    <button
      @click="handleBackClick"
      aria-label="Go Back"
      class="cb-device-test-fetch__back-button"
      v-if="manualTestRequired"
    >
      <img alt src="../assets/icons/back-arrow.svg" />
    </button>
  </div>
</template>

<script>
/* global gtag */
import { mapState, mapActions, mapMutations } from 'vuex';

const waitTime = parseInt(process.env.VUE_APP_POLLING_WAIT_TIME, 10);
const intervalTime = parseInt(process.env.VUE_APP_POLLING_INTERVAL_TIME, 10);
const totalTime = parseInt(process.env.VUE_APP_POLLING_OVERALL_TIME, 10);

const waitTimePercent = waitTime / totalTime;
const intervalTimePercent = intervalTime / totalTime;

export default {
  data() {
    return {
      title: 'Sending Signal',
      body: 'This could take a few minutes',
      circleAnimation: `transition: ${waitTime}s linear all`,
      circleDasharray: '0 283',
      setAriaInterval: null,
      progress: 0,
    };
  },
  mounted() {
    this.$refs.title.focus();
    document.title = 'CellBridge - Sending Signal';
    this.setDeviceTestStartTime(new Date());
    this.pollDeviceStatus(process.env.VUE_APP_POLLING_WAIT_TIME);

    // Wait 50ms to set circle dasharray b/c of issue on ios
    setTimeout(() => {
      this.circleDasharray = `${waitTimePercent * 283} 283`;
    }, 50);

    this.setAriaInterval = setInterval(() => {
      if (this.progress === 100) {
        clearInterval(this.setAriaInterval);
        return;
      }
      this.updateAriaLabel();
    }, 1000);
  },
  methods: {
    handleBackClick() {
      this.$router.push('/setup-test');
      gtag('event', 'goBackClick', {
        event_label: 'go back',
        event_category: 'deviceTest',
        value: 1,
      });
    },
    updateAriaLabel() {
      const currentTime = new Date();
      const currentTestDuration = (currentTime - this.deviceTestStartTime) / 1000;
      const percentageComplete = currentTestDuration / totalTime;
      this.progress = Math.ceil(percentageComplete * 100);
    },
    ...mapActions(['pollDeviceStatus']),
    ...mapMutations([
      'clearPollingTimeout',
      'setDeviceTestStartTime',
      'updateDeviceTestStep',
      'setPollingCounter',
    ]),
  },
  watch: {
    pollingCounter() {
      this.circleAnimation = `transition: ${intervalTime}s linear all`;
      this.title = 'Finishing Up';
      this.body = 'Go ahead and <strong>disarm your system</strong> to stop the siren.';

      if (this.pollingCounter === -1) {
        this.circleDasharray = '283 283';
        setTimeout(() => {
          this.updateDeviceTestStep('cbDeviceTestSuccess');
        }, intervalTime * 1000);
        return;
      }

      this.circleDasharray = `${
        waitTimePercent * 283 + intervalTimePercent * this.pollingCounter * 283
      } 283`;
    },
  },
  computed: {
    ...mapState({
      manualTestRequired: (state) => state.userInfo.manualTestRequired,
      pollingCounter: (state) => state.pollingCounter,
      deviceTestStartTime: (state) => state.deviceTestStartTime,
    }),
  },
  beforeDestroy() {
    this.clearPollingTimeout();
    this.setPollingCounter(0);
    clearInterval(this.setAriaInterval);
  },
};
</script>

<style lang="scss" scoped>
.cb-device-test-fetch {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  max-width: 335px;
  margin: 0 auto;
  padding: 67px 20px 20px;
  box-sizing: border-box;

  &__progress {
    position: relative;
    width: 280px;
    height: 280px;
    margin-bottom: 22px;

    &__svg {
      transform: scaleX(-1);
    }

    &__circle {
      fill: none;
      stroke: none;
    }

    &__path-elapsed {
      stroke-width: 3px;
      stroke: white;
      stroke-opacity: 0.3;
    }

    &__path-remaining {
      stroke-width: 3px;
      transform: rotate(90deg);
      transform-origin: center;
      fill-rule: nonzero;
      stroke: currentColor;
    }

    &__label {
      position: absolute;
      width: 280px;
      height: 280px;
      top: 0;
      display: flex;
      align-items: center;
      justify-content: center;

      span {
        width: 150px;
      }
    }
  }

  &__container {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__heading {
    font-weight: 600;
    text-align: center;
  }

  &__body {
    max-width: 270px;
    font-size: 18px;
    line-height: 27px;
    text-align: center;
  }

  &__footer {
    width: 100%;

    .cb-button {
      display: none;
    }
  }

  &__back-button {
    border: none;
    background-color: unset;
    position: absolute;
    top: 20px;
    left: 20px;
    padding: 0;
  }

  @include desktop {
    height: 100%;
    max-width: none;
    padding: 20px;

    &__progress {
      margin: 50px 0 22px;
    }

    &__container {
      flex-grow: 1;
      margin-bottom: 0;
    }

    &__footer {
      display: flex;
      flex-direction: column;
    }

    &__divider {
      height: 1px;
      width: 100%;
      background-color: white;
    }

    .cb-button {
      display: block;
      margin-top: 20px;
      width: 128px;
    }

    &__back-button {
      display: none;
    }
  }
}
</style>
